import React, { useState, useCallback } from 'react';
import { useFeedback } from '../contexts/FeedbackContext';
import { useAuth } from './AuthContext';
import styles from './FeedbackPanel.module.css';

const HOW_DID_GO_OPTIONS = [
  'Worked as expected',
  'Worked, but had rough edges',
  "Didn't work as expected",
  "I couldn't do what I needed here",
];

const SPEED_OPTIONS = ['Fast & clear', 'Acceptable', 'Slow and/or confusing'];

const USEFULNESS_OPTIONS = ['1', '2', '3', '4', '5', 'N/A - this page has no generated content'];

const SEVERITY_OPTIONS = ['Minor', 'Moderate', 'Major', 'Blocking'];

const WHERE_ISSUE_OPTIONS = [
  'Layout / navigation',
  'Prompts & controls',
  'Upload / attachments',
  'Generated content quality',
  'Cultural fit / place-based alignment',
  'Translation / bilingual',
  'Standards locate/apply',
  'Sources / citations',
  'Export / copy / print',
  'Account / permissions',
  'Speed / loading / errors',
];

const PRIORITY_OPTIONS = ['Must fix', 'Should fix', 'Nice to have'];

const FEELING_OPTIONS = [
  { value: 'amazed', label: 'amazed', emoji: '🤩' },
  { value: 'satisfied', label: 'satisfied', emoji: '🙂' },
  { value: 'confident', label: 'confident', emoji: '😎' },
  { value: 'cautious', label: 'cautious', emoji: '🙂' },
  { value: 'skeptical', label: 'skeptical', emoji: '🤔' },
  { value: 'disappointed', label: 'disappointed', emoji: '😞' },
  { value: 'confused', label: 'confused', emoji: '😵' },
  { value: 'frustrated', label: 'frustrated', emoji: '😠' },
  { value: 'overwhelmed', label: 'overwhelmed', emoji: '😩' },
];

function ScreenFeedbackForm({ context, onSuccess, onCancel }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    how_did_go: '',
    which_part: [],
    which_part_other: '',
    speed_clarity: '',
    usefulness: '',
    what_worked: '',
    what_didnt: '',
    severity: '',
    where_issue: [],
    where_issue_other: '',
    priority: '',
    feelings: [],
    other_feedback: '',
  });

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggleArray = (key, item) => {
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(item) ? prev[key].filter((x) => x !== item) : [...prev[key], item],
    }));
  };

  const pageElements = context.page_elements || [];
  const canNextStep1 = form.how_did_go && (form.which_part.length > 0 || form.which_part_other.trim());
  const canNextStep2 = form.speed_clarity && form.usefulness;
  const canNextStep3 = form.what_worked.trim() && form.what_didnt.trim() && form.severity;
  const canNextStep4 = form.where_issue.length > 0 || form.where_issue_other.trim();
  const canSubmitStep4 = canNextStep4 && form.priority && form.feelings.length > 0;

  const handleSubmit = async () => {
    if (!canSubmitStep4) return;
    setError('');
    setSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/feedback/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Token ${token}` }),
        },
        body: JSON.stringify({
          feedback_type: 'screen',
          page_id: context.page_id,
          page_name: context.page_name,
          lesson_id: context.lesson_id || null,
          page_elements: context.page_elements,
          payload: form,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit feedback');
      }
      const data = await response.json();
      onSuccess(data);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.body}>
        <div className={styles.rewardBanner}>
          <img src="/assets/gift_reward_icon.png" alt="" className={styles.rewardBannerIcon} aria-hidden />
          <span className={styles.rewardBannerText}>
            <strong>Get Rewarded!</strong>
            <br />
            Submit 5 feedback surveys to receive $10 gift card as a thank you for helping us improve!
          </span>
        </div>

        {step === 1 && (
          <>
            <div className={styles.stepIndicator}>How did this page go? (1/4)</div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                On {context.page_name || 'this page'}, how did things go just now? <span className={styles.required}>*</span>
              </label>
              <div className={styles.radioGroup}>
                {HOW_DID_GO_OPTIONS.map((opt) => (
                  <label key={opt} className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="how_did_go"
                      value={opt}
                      checked={form.how_did_go === opt}
                      onChange={() => update('how_did_go', opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                Which part of this page were you using? <span className={styles.required}>*</span>
              </label>
              <div className={styles.checkboxGroup}>
                {pageElements.map((el) => (
                  <label key={el} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.which_part.includes(el)}
                      onChange={() => toggleArray('which_part', el)}
                    />
                    {el}
                  </label>
                ))}
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={!!form.which_part_other.trim()}
                    onChange={(e) => update('which_part_other', e.target.checked ? form.which_part_other : '')}
                  />
                  Other:
                </label>
                <input
                  type="text"
                  className={styles.textInput}
                  placeholder="Specify other"
                  value={form.which_part_other}
                  onChange={(e) => update('which_part_other', e.target.value)}
                />
              </div>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.cancelBtn} onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.sendBtn}
                disabled={!canNextStep1}
                onClick={() => setStep(2)}
              >
                Next
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className={styles.stepIndicator}>Quick ratings (2/4)</div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                Perceived speed & clarity <span className={styles.required}>*</span>
              </label>
              <div className={styles.radioGroup}>
                {SPEED_OPTIONS.map((opt) => (
                  <label key={opt} className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="speed_clarity"
                      value={opt}
                      checked={form.speed_clarity === opt}
                      onChange={() => update('speed_clarity', opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                Usefulness of what this page produced <span className={styles.required}>*</span>
              </label>
              <div className={styles.radioGroup}>
                {USEFULNESS_OPTIONS.map((opt) => (
                  <label key={opt} className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="usefulness"
                      value={opt}
                      checked={form.usefulness === opt}
                      onChange={() => update('usefulness', opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.cancelBtn} onClick={onCancel}>
                Cancel
              </button>
              <button type="button" className={styles.sendBtn} onClick={() => setStep(1)}>
                Back
              </button>
              <button
                type="button"
                className={styles.sendBtn}
                disabled={!canNextStep2}
                onClick={() => setStep(3)}
              >
                Next
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className={styles.stepIndicator}>What worked / what didn't (3/4)</div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                What worked well on this page? <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={styles.textInput}
                placeholder="Short answer"
                value={form.what_worked}
                onChange={(e) => update('what_worked', e.target.value)}
              />
            </div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                What didn't work or was confusing? <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={styles.textInput}
                placeholder="Short answer"
                value={form.what_didnt}
                onChange={(e) => update('what_didnt', e.target.value)}
              />
            </div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                Severity <span className={styles.required}>*</span>
              </label>
              <div className={styles.radioGroup}>
                {SEVERITY_OPTIONS.map((opt) => (
                  <label key={opt} className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="severity"
                      value={opt}
                      checked={form.severity === opt}
                      onChange={() => update('severity', opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.cancelBtn} onClick={onCancel}>
                Cancel
              </button>
              <button type="button" className={styles.sendBtn} onClick={() => setStep(2)}>
                Back
              </button>
              <button
                type="button"
                className={styles.sendBtn}
                disabled={!canNextStep3}
                onClick={() => setStep(4)}
              >
                Next
              </button>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div className={styles.stepIndicator}>Final Feedback (4/4)</div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                Where's the issue? (Checkboxes) <span className={styles.required}>*</span>
              </label>
              <div className={styles.checkboxGroup}>
                {WHERE_ISSUE_OPTIONS.map((opt) => (
                  <label key={opt} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.where_issue.includes(opt)}
                      onChange={() => toggleArray('where_issue', opt)}
                    />
                    {opt}
                  </label>
                ))}
                <label className={styles.checkboxLabel}>Other:</label>
                <input
                  type="text"
                  className={styles.textInput}
                  placeholder="Describe other issue"
                  value={form.where_issue_other}
                  onChange={(e) => update('where_issue_other', e.target.value)}
                />
              </div>
            </div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                Priority <span className={styles.required}>*</span>
              </label>
              <div className={styles.radioGroup}>
                {PRIORITY_OPTIONS.map((opt) => (
                  <label key={opt} className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="priority"
                      value={opt}
                      checked={form.priority === opt}
                      onChange={() => update('priority', opt)}
                    />
                    <em>{opt}</em>
                  </label>
                ))}
              </div>
            </div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                How are you feeling about Kumu Connect today? (Select all that apply) <span className={styles.required}>*</span>
              </label>
              <div className={styles.checkboxGroup}>
                {FEELING_OPTIONS.map(({ value, label, emoji }) => (
                  <label key={value} className={styles.feelingOption}>
                    <input
                      type="checkbox"
                      checked={form.feelings.includes(value)}
                      onChange={() => toggleArray('feelings', value)}
                    />
                    <span>{emoji}</span>
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>Any other feedback/comments/ideas about Kumu Connect?</label>
              <textarea
                className={styles.textArea}
                placeholder="Paragraph"
                value={form.other_feedback}
                onChange={(e) => update('other_feedback', e.target.value)}
              />
            </div>
            {error && <p className={styles.errorText}>{error}</p>}
            <div className={styles.actions}>
              <button type="button" className={styles.cancelBtn} onClick={onCancel}>
                Cancel
              </button>
              <button type="button" className={styles.sendBtn} onClick={() => setStep(3)}>
                Back
              </button>
              <button
                type="button"
                className={styles.sendBtn}
                disabled={!canSubmitStep4 || submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Sending…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Overall Session / Lesson Feedback (view-lesson-plan) – 5 sections
const LESSON_FINISHED_OPTIONS = ['Yes fully', 'Partially', 'No'];
const LESSON_USEFULNESS_OPTIONS = ['1 - not useful at all', '2', '3', '4', '5 - very useful'];
const FELT_SOLID_OPTIONS = [
  'Content accuracy',
  'Cultural fit / place-based alignment',
  'Standards alignment',
  'Language/translation quality',
  'Source quality / citations',
  'Classroom readiness (materials, timing)',
  'Differentiation / scaffolds',
  'Assessment items',
  'None of the above / N/A',
];
const USED_WITH_STUDENTS_OPTIONS = ['Yes', 'Not yet', 'No'];
const AREAS_NEED_WORK_OPTIONS = [
  'Standards locate/apply',
  'Prompting flow',
  'Generated content quality',
  'Cultural fit / place-based alignment',
  'Translation / bilingual features',
  'Sources / citations',
  'Export / copy / print',
  'Class materials (handouts, slides)',
  'Account / permissions',
  'Speed / loading / errors',
];

function LessonPlanFeedbackForm({ context, onSuccess, onCancel }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    today_used_tool: '',
    finished_task: '',
    usefulness_lessons: '',
    speed_clarity: '',
    felt_solid: [],
    felt_solid_other: '',
    used_with_students: '',
    if_yes_how_did_it_go: '',
    top_improvements: '',
    bugs_blockers: '',
    areas_need_work: [],
    recommend_score: '',
    feelings: [],
    other_feedback: '',
  });

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggleArray = (key, item) => {
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(item) ? prev[key].filter((x) => x !== item) : [...prev[key], item],
    }));
  };

  const canStep1 = form.today_used_tool.trim() && form.finished_task;
  const canStep2 = form.usefulness_lessons && form.speed_clarity;
  const canStep3 = form.felt_solid.length > 0 || form.felt_solid_other.trim();
  const canStep3WithStudents = canStep3 && form.used_with_students;
  const needHowDidItGo = form.used_with_students === 'Yes';
  const canStep4 = form.top_improvements.trim() && (!needHowDidItGo || form.if_yes_how_did_it_go.trim());
  const canStep5 = form.recommend_score !== '' && form.feelings.length > 0;
  const recommendNum = parseInt(form.recommend_score, 10);
  const validRecommend = !isNaN(recommendNum) && recommendNum >= 0 && recommendNum <= 10;

  const handleSubmit = async () => {
    if (!canStep5 || !validRecommend) return;
    setError('');
    setSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/feedback/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Token ${token}` }),
        },
        body: JSON.stringify({
          feedback_type: 'lesson_plan',
          page_id: context.page_id,
          page_name: context.page_name,
          lesson_id: context.lesson_id || null,
          page_elements: context.page_elements,
          payload: { ...form, recommend_score: recommendNum },
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit feedback');
      }
      const data = await response.json();
      onSuccess(data);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.body}>
      <div className={styles.rewardBanner}>
        <img src="/assets/gift_reward_icon.svg" alt="" className={styles.rewardBannerIcon} aria-hidden />
        <span className={styles.rewardBannerText}>
          <strong>Get Rewarded!</strong>
          <br />
          Submit 5 feedback surveys to receive $10 gift card as a thank you for helping us improve!
        </span>
      </div>
      <p className={styles.sectionSubtitle}>Overall Session / Lesson Feedback (≤5 min)</p>

      {step === 1 && (
        <>
          <div className={styles.stepIndicator}>Session context (1/5)</div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>
              Today I used the tool to... <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              className={styles.textInput}
              placeholder="Short answer"
              value={form.today_used_tool}
              onChange={(e) => update('today_used_tool', e.target.value)}
            />
          </div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>
              Did you finish your task this session? <span className={styles.required}>*</span>
            </label>
            <div className={styles.radioGroup}>
              {LESSON_FINISHED_OPTIONS.map((opt) => (
                <label key={opt} className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="finished_task"
                    value={opt}
                    checked={form.finished_task === opt}
                    onChange={() => update('finished_task', opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
            <button type="button" className={styles.sendBtn} disabled={!canStep1} onClick={() => setStep(2)}>Next</button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className={styles.stepIndicator}>Core ratings (2/5)</div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>
              Usefulness of generated lesson(s) <span className={styles.required}>*</span>
            </label>
            <div className={styles.radioGroup}>
              {LESSON_USEFULNESS_OPTIONS.map((opt) => (
                <label key={opt} className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="usefulness_lessons"
                    value={opt}
                    checked={form.usefulness_lessons === opt}
                    onChange={() => update('usefulness_lessons', opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>
              Perceived speed & clarity across the tool <span className={styles.required}>*</span>
            </label>
            <div className={styles.radioGroup}>
              {SPEED_OPTIONS.map((opt) => (
                <label key={opt} className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="speed_clarity_lesson"
                    value={opt}
                    checked={form.speed_clarity === opt}
                    onChange={() => update('speed_clarity', opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
            <button type="button" className={styles.sendBtn} onClick={() => setStep(1)}>Back</button>
            <button type="button" className={styles.sendBtn} disabled={!canStep2} onClick={() => setStep(3)}>Next</button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div className={styles.stepIndicator}>Quality & fit (3/5)</div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>
              Check all that felt solid today (Checkboxes) <span className={styles.required}>*</span>
            </label>
            <div className={styles.checkboxGroup}>
              {FELT_SOLID_OPTIONS.map((opt) => (
                <label key={opt} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.felt_solid.includes(opt)}
                    onChange={() => toggleArray('felt_solid', opt)}
                  />
                  {opt}
                </label>
              ))}
              <label className={styles.checkboxLabel}>Other:</label>
              <input
                type="text"
                className={styles.textInput}
                placeholder="Other"
                value={form.felt_solid_other}
                onChange={(e) => update('felt_solid_other', e.target.value)}
              />
            </div>
          </div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>
              Did you use the output with students (even partly)? <span className={styles.required}>*</span>
            </label>
            <div className={styles.radioGroup}>
              {USED_WITH_STUDENTS_OPTIONS.map((opt) => (
                <label key={opt} className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="used_with_students"
                    value={opt}
                    checked={form.used_with_students === opt}
                    onChange={() => update('used_with_students', opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
            <button type="button" className={styles.sendBtn} onClick={() => setStep(2)}>Back</button>
            <button type="button" className={styles.sendBtn} disabled={!canStep3WithStudents} onClick={() => setStep(4)}>Next</button>
          </div>
        </>
      )}

      {step === 4 && (
        <>
          <div className={styles.stepIndicator}>Improvements & issues (4/5)</div>
          {needHowDidItGo && (
            <div className={styles.questionBlock}>
              <label className={styles.questionLabel}>
                → If Yes: How did it go? <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={styles.textInput}
                placeholder="Short answer"
                value={form.if_yes_how_did_it_go}
                onChange={(e) => update('if_yes_how_did_it_go', e.target.value)}
              />
            </div>
          )}
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>
              Top 1-2 improvements we should make next <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              className={styles.textInput}
              placeholder="Short answer"
              value={form.top_improvements}
              onChange={(e) => update('top_improvements', e.target.value)}
            />
          </div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>Any bugs or blockers today?</label>
            <input
              type="text"
              className={styles.textInput}
              placeholder="Short answer"
              value={form.bugs_blockers}
              onChange={(e) => update('bugs_blockers', e.target.value)}
            />
          </div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>Which areas need the most work? (Checkboxes)</label>
            <div className={styles.checkboxGroup}>
              {AREAS_NEED_WORK_OPTIONS.map((opt) => (
                <label key={opt} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.areas_need_work.includes(opt)}
                    onChange={() => toggleArray('areas_need_work', opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
          {error && <p className={styles.errorText}>{error}</p>}
          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
            <button type="button" className={styles.sendBtn} onClick={() => setStep(3)}>Back</button>
            <button type="button" className={styles.sendBtn} disabled={!canStep4} onClick={() => setStep(5)}>Next</button>
          </div>
        </>
      )}

      {step === 5 && (
        <>
          <div className={styles.stepIndicator}>Final Feedback (5/5)</div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>
              How likely are you to recommend this tool to a colleague? <span className={styles.required}>*</span>
            </label>
            <div className={styles.scaleRow} style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <label key={n} className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="recommend_score"
                    value={String(n)}
                    checked={form.recommend_score === String(n)}
                    onChange={() => update('recommend_score', String(n))}
                  />
                  {n}
                </label>
              ))}
            </div>
          </div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>
              How are you feeling about Kumu Connect today? (Select all that apply) <span className={styles.required}>*</span>
            </label>
            <div className={styles.checkboxGroup}>
              {FEELING_OPTIONS.map(({ value, label, emoji }) => (
                <label key={value} className={styles.feelingOption}>
                  <input
                    type="checkbox"
                    checked={form.feelings.includes(value)}
                    onChange={() => toggleArray('feelings', value)}
                  />
                  <span>{emoji}</span>
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>Any other feedback/comments/ideas about Kumu Connect?</label>
            <textarea
              className={styles.textArea}
              placeholder="Paragraph"
              value={form.other_feedback}
              onChange={(e) => update('other_feedback', e.target.value)}
            />
          </div>
          {error && <p className={styles.errorText}>{error}</p>}
          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
            <button type="button" className={styles.sendBtn} onClick={() => setStep(4)}>Back</button>
            <button
              type="button"
              className={styles.sendBtn}
              disabled={!canStep5 || !validRecommend || submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function FeedbackPanel() {
  const { isOpen, variant, context, closeFeedbackPanel } = useFeedback();
  const { refreshUserProfile } = useAuth();
  const [submitted, setSubmitted] = useState(false);

  const [lastSuccessRefilled, setLastSuccessRefilled] = useState(false);
  const handleSuccess = useCallback(
    async (data) => {
      if (data.refilled && data.generations_remaining != null) {
        await refreshUserProfile();
      }
      setLastSuccessRefilled(!!data.refilled);
      setSubmitted(true);
      setTimeout(() => {
        closeFeedbackPanel();
        setSubmitted(false);
      }, 1500);
    },
    [refreshUserProfile, closeFeedbackPanel]
  );

  const handleCancel = useCallback(() => {
    closeFeedbackPanel();
  }, [closeFeedbackPanel]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className={styles.overlay}
        onClick={handleCancel}
        aria-hidden="true"
      />
      <div className={styles.panel} role="dialog" aria-labelledby="feedback-panel-title">
        <div className={styles.header}>
          <h2 id="feedback-panel-title" className={styles.title}>
            Feedback
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleCancel}
            aria-label="Close feedback panel"
          >
            ×
          </button>
        </div>
        {submitted ? (
          <div className={styles.body} style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '1rem', color: '#137A55' }}>
              Thank you! Your feedback has been submitted.
              {lastSuccessRefilled && ' Your creations have been refilled.'}
            </p>
          </div>
        ) : variant === 'lesson_plan' ? (
          <LessonPlanFeedbackForm context={context} onSuccess={handleSuccess} onCancel={handleCancel} />
        ) : (
          <ScreenFeedbackForm context={context} onSuccess={handleSuccess} onCancel={handleCancel} />
        )}
      </div>
    </>
  );
}
